import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ChevronDown, ChevronUp, AlertCircle, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SkippedRecord } from '@/types/investor-import';

interface SkippedRecordsTableProps {
  skippedRecords: SkippedRecord[];
}

export const SkippedRecordsTable = ({
  skippedRecords,
}: SkippedRecordsTableProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sortBy, setSortBy] = useState<'row' | 'reason'>('row');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Group records by reason
  const groupedByReason = useMemo(() => {
    const groups: Record<string, SkippedRecord[]> = {};
    skippedRecords.forEach((record) => {
      const reason = record.reason || 'Unknown reason';
      if (!groups[reason]) {
        groups[reason] = [];
      }
      groups[reason].push(record);
    });
    return groups;
  }, [skippedRecords]);

  // Sort records
  const sortedRecords = useMemo(() => {
    const sorted = [...skippedRecords];
    sorted.sort((a, b) => {
      if (sortBy === 'row') {
        return sortOrder === 'asc'
          ? a.row_number - b.row_number
          : b.row_number - a.row_number;
      } else {
        const aReason = a.reason || '';
        const bReason = b.reason || '';
        return sortOrder === 'asc'
          ? aReason.localeCompare(bReason)
          : bReason.localeCompare(aReason);
      }
    });
    return sorted;
  }, [skippedRecords, sortBy, sortOrder]);

  const handleSort = (column: 'row' | 'reason') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const downloadCSV = () => {
    const headers = ['Row Number', 'Name', 'Fund', 'Reason', 'Action Required'];
    const rows = skippedRecords.map((record) => [
      record.row_number,
      record.investor_name || record.contact_name || '',
      record.fund_name || '',
      record.reason,
      record.action_required || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'skipped-investor-records.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (skippedRecords.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Skipped Records ({skippedRecords.length})
            </CardTitle>
            <CardDescription>
              These records were skipped during import and require manual
              attention
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadCSV}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-2" />
                  Expand
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          {/* Summary by reason */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Summary by Reason
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(groupedByReason).map(([reason, records]) => (
                <div
                  key={reason}
                  className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                >
                  <span className="text-sm text-gray-700">{reason}</span>
                  <Badge variant="secondary">{records.length}</Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('row')}
                  >
                    <div className="flex items-center gap-1">
                      Row #
                      {sortBy === 'row' &&
                        (sortOrder === 'asc' ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Fund</TableHead>
                  <TableHead
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('reason')}
                  >
                    <div className="flex items-center gap-1">
                      Reason
                      {sortBy === 'reason' &&
                        (sortOrder === 'asc' ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        ))}
                    </div>
                  </TableHead>
                  <TableHead>Action Required</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRecords.slice(0, 20).map((record) => (
                  <TableRow key={record.row_number}>
                    <TableCell className="font-mono text-sm">
                      {record.row_number}
                    </TableCell>
                    <TableCell>
                      {record.investor_name || record.contact_name || '-'}
                      {record.contact_name && (
                        <span className="text-xs text-gray-500 ml-1">
                          (contact)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{record.fund_name || '-'}</TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'text-sm',
                          record.reason.includes('No mapped funds') &&
                            'text-amber-700',
                          record.reason.includes('No commitment amount') &&
                            'text-orange-700',
                          record.reason.includes('Could not match') &&
                            'text-red-700'
                        )}
                      >
                        {record.reason}
                      </span>
                    </TableCell>
                    <TableCell>
                      {record.action_required && (
                        <span className="text-sm text-gray-600">
                          {record.action_required}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {sortedRecords.length > 20 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-sm text-gray-500 py-4"
                    >
                      ... and {sortedRecords.length - 20} more records
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
    </Card>
  );
};
